import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { AuthContext } from '../../../../context/AuthContext';
import { slugify } from '../../../../utils/slugify';
import { supabase } from '../../../../utils/supabase';
import FilterPane from '../../../table/FilterPane';

function CountyRankings({
  selectedCountryX,
  selectedCounty,
  selectedCountySlug,
  golfCourses: golfCoursesProp = [],
}) {
  const { user } = useContext(AuthContext);
  const [upvotedList, setUpvotedList] = useState([]);
  const [upvoteCounts, setUpvoteCounts] = useState([]);

  const golfCourses = golfCoursesProp;

  const getCountryPrefix = (selectedCountryX) => {
    if (!selectedCountryX) return '';
    switch (selectedCountryX.toLowerCase()) {
      case 'england':
        return 'england';
      case 'scotland':
        return 'scotland';
      case 'ireland':
        return 'ireland';
      case 'northern ireland':
        return 'ni';
      case 'wales':
        return 'wales';
      case 'usa':
        return 'usa';
      default:
        return '';
    }
  };

  const getRankAccessor = useCallback((selectedCountryX) => {
    const prefix = getCountryPrefix(selectedCountryX);
    return `${prefix}_rank_gl`;
  }, []);

  // Define a function to handle the upvote button click
  const handleUpvoteClick = useCallback(
    async (courseName) => {
      // Check if user is logged in
      if (!user) {
        alert(
          'Sign in to golflists.com to upvote courses you love! We use your feedback to adjust our rankings.',
        );
        return;
      }
      try {
        const { data: existingVotes, error } = await supabase
          .from('course_votes')
          .select('*')
          .eq('user_that_voted', user.id)
          .eq('course_name', courseName);

        if (error) {
          console.error('Error fetching existing votes:', error);
          return;
        }

        const existingVote = existingVotes.length > 0 ? existingVotes[0] : null;

        if (existingVote && existingVote.id) {
          await supabase
            .from('course_votes')
            .update({
              course_upvoted: !existingVote.course_upvoted,
            })
            .eq('id', existingVote.id);
          //console.log(`Hello existingvote check ${existingVote.id}`);

          const updatedUpvotedList = upvotedList.map((item) =>
            item.course_name === courseName
              ? { ...item, course_upvoted: !existingVote.course_upvoted }
              : item,
          );
          setUpvotedList(updatedUpvotedList);
        } else {
          // ...
          const { error, status } = await supabase
            .from('course_votes')
            .insert({
              course_name: courseName,
              course_upvoted: true,
              course_downvoted: false,
              user_that_voted: user.id,
            })
            .single();

          //console.log("Insert error:", error);
          //console.log("Insert status:", status);

          if (error && !error.message.includes('violates unique constraint')) {
            console.error('Error details1:', error);
            console.error('Error status:', error.status);
            console.error('Error response:', error.response);
            return;
          }

          if (status === 201) {
            const updatedUpvotedList = [
              ...upvotedList,
              {
                course_name: courseName,
                course_upvoted: true,
                course_downvoted: false,
                user_that_voted: user.id,
              },
            ];
            setUpvotedList(updatedUpvotedList);
          } else {
            console.error('Error details2:', error);
          }
        }
        await fetchUpvoteCounts();
      } catch (error) {
        console.error(error);
      }
    },
    [upvotedList, user],
  );

  const columns = React.useMemo(
    () => [
      {
        Header: 'Upvotes',
        accessor: 'id',
        width: '10px',
        disableFilter: true,
        headerClassName: 'text-center',
        Cell: ({ row }) => {
          const courseName = row.original.course_name;
          const isUpvoted =
            user &&
            courseName &&
            upvotedList.length > 0 &&
            upvotedList.some(
              (item) =>
                item.course_name === courseName &&
                item.user_that_voted === user.id &&
                item.course_upvoted === true,
            );

          const upvoteCount = upvoteCounts.find(
            (item) => item.course_name === courseName,
          )?.upvote_count;

          return (
            <div className="mx-auto w-12 border border-solid rounded-md border-gray-300 flex justify-center items-center">
              {isUpvoted ? (
                <FontAwesomeIcon
                  icon={solid('circle-up')}
                  className="text-green-500 mx-auto hover:cursor-pointer"
                  onClick={() => handleUpvoteClick(courseName)}
                />
              ) : (
                <FontAwesomeIcon
                  icon={solid('circle-up')}
                  className="text-slate-400 mx-auto hover:cursor-pointer"
                  onClick={() => handleUpvoteClick(courseName)}
                />
              )}
              <span className="mr-2">{upvoteCount || 0}</span>
            </div>
          );
        },
      },
      {
        Header: 'Course',
        accessor: 'course_name',
        Cell: ({ row }) => {
          const { original } = row || {};
          const { course_name, distance } = original || {};

          return (
            <div className="flex flex-col flex-nowrap justify-center items-start text-left">
              {course_name && (
                <Link
                  to={`/lists/golf-courses/${slugify(course_name)}`}
                  className="bg-indigo-800 hover:bg-indigo-600 text-white p-1 rounded-lg"
                >
                  {course_name}
                </Link>
              )}
              {distance && (
                <div style={{ fontSize: '0.9em', color: 'green' }}>
                  {`${(distance / 1000).toFixed(2)} km`}
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: 'Country Rank',
        accessor: getRankAccessor(selectedCountryX),
        disableFilter: true,
        width: '10px',
        defaultSortDesc: false,
      },
      {
        Header: 'County', // The header of the column
        accessor: 'course_county', // The accessor key for this column
      },
      {
        Header: 'County Rank', // The header of the column
        accessor: 'county_rank', // The accessor key for this column
        disableFilter: true,
      },
      {
        Header: 'Style', // The header of the column
        accessor: 'course_style', // The accessor key for this column
      },
      {
        Header: 'GolfList Collections', // The header of the column
        accessor: 'collections', // The accessor key for this column
        Cell: ({ cell }) => (
          <div className="flex flex-wrap">
            {cell.value &&
              cell.value.map((collection, index) => (
                <div key={index} className="mb-1">
                  <span className="mr-1 inline-flex items-center rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                    {collection}
                  </span>
                </div>
              ))}
          </div>
        ),
      },
      {
        Header: 'Original Designer(s)', // The header of the column
        accessor: 'combined_designer_original', // The accessor key for this column
        width: '10px',
        Cell: ({ cell }) => (
          <div>
            {cell.value &&
              cell.value.map((contributor, index) => (
                <div key={index} className="mb-1">
                  <span className="inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                    {contributor}
                  </span>
                </div>
              ))}
          </div>
        ),
      },
      {
        Header: 'Design Contributors', // The header of the column
        accessor: 'designer_contributor', // The accessor key for this column
        width: '10px',
        Cell: ({ cell }) => (
          <div>
            {cell.value &&
              cell.value.map((contributor, index) => (
                <div key={index} className="mb-1">
                  <span className="inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                    {contributor}
                  </span>
                </div>
              ))}
          </div>
        ),
      },
    ],
    [
      handleUpvoteClick,
      upvotedList,
      upvoteCounts,
      selectedCountryX,
      user,
      getRankAccessor,
    ],
  );

  useEffect(() => {
    const fetchUpvoteData = async () => {
      try {
        if (user) {
          const { data: upvotedData } = await supabase
            .from('course_votes')
            .select('*')
            .eq('user_that_voted', user.id);
          setUpvotedList(upvotedData ?? []);
        } else {
          setUpvotedList([]);
        }
        await fetchUpvoteCounts();
      } catch (err) {
        console.error('Error fetching upvote data:', err);
      }
    };
    fetchUpvoteData();
  }, [user]);

  const fetchUpvoteCounts = async () => {
    try {
      const { data, error } = await supabase.rpc('fetch_upvote_counts');
      if (error) {
        console.error('Error fetching upvote counts:', error);
        return;
      }
      setUpvoteCounts(data ?? []);
    } catch (error) {
      console.error('Error fetching upvote counts:', error);
    }
  };

  return (
    <FilterPane
      data={golfCourses}
      columns={columns}
      defaultCountry={selectedCountryX}
    />
  );
}

export default CountyRankings;
